import React from 'react';
import LegalPage from './LegalPage';

export default function CookiePolicy() {
  return (
    <LegalPage title="Cookie Policy" updated="April 15, 2026">

      <p>This Cookie Policy explains how ClickSaver ("we", "us", or "our") uses cookies and similar tracking technologies when you visit our website or use our services. By using our website, you consent to our use of cookies as described in this policy.</p>

      <div className="legal-toc">
        <strong style={{ fontSize: 14, color: '#0f172a' }}>Table of Contents</strong>
        <ul style={{ marginTop: 10 }}>
          <li><a href="#what">1. What Are Cookies?</a></li>
          <li><a href="#types">2. Types of Cookies We Use</a></li>
          <li><a href="#third-party">3. Third-Party Cookies</a></li>
          <li><a href="#tracking">4. Tracking Script (Customer Sites)</a></li>
          <li><a href="#control">5. How to Control Cookies</a></li>
          <li><a href="#changes">6. Changes to This Policy</a></li>
          <li><a href="#contact">7. Contact Us</a></li>
        </ul>
      </div>

      <h2 id="what">1. What Are Cookies?</h2>
      <p>Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners.</p>
      <p>Similar technologies include web beacons, pixel tags, and local storage — these work in a similar way to cookies and are covered by this policy.</p>

      <div className="legal-divider" />

      <h2 id="types">2. Types of Cookies We Use</h2>

      <h3>Essential Cookies</h3>
      <p>These cookies are strictly necessary for the ClickSaver platform to function. They cannot be switched off in our systems.</p>
      <ul>
        <li><strong>Session cookies:</strong> Keep you logged in to the dashboard during your visit</li>
        <li><strong>Security cookies:</strong> Used for authentication and to prevent cross-site request forgery (CSRF)</li>
        <li><strong>Preference cookies:</strong> Remember your settings such as theme preference and active campaign</li>
      </ul>

      <h3>Analytics Cookies</h3>
      <p>These help us understand how visitors interact with our website, so we can improve the experience.</p>
      <ul>
        <li><strong>Usage analytics:</strong> Pages visited, features used, and session duration within the dashboard</li>
        <li><strong>Error tracking:</strong> We log errors to help diagnose and fix issues</li>
      </ul>

      <h3>Functional Cookies</h3>
      <p>These cookies allow the website to provide enhanced functionality and personalisation.</p>
      <ul>
        <li><strong>localStorage (campaign selection):</strong> Remembers which campaign you last selected</li>
        <li><strong>Theme preference:</strong> Stores your dark/light mode setting</li>
      </ul>

      <div className="legal-divider" />

      <h2 id="third-party">3. Third-Party Cookies</h2>
      <p>Some third-party services we use may set their own cookies:</p>
      <ul>
        <li><strong>Firebase (Google):</strong> Used for authentication. Firebase may set cookies to manage user sessions. Subject to <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.</li>
        <li><strong>Stripe:</strong> Used to process payments. Stripe may set cookies during the checkout flow. Subject to <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a>.</li>
      </ul>
      <p>We do not use cookies for advertising or remarketing purposes on our own website.</p>

      <div className="legal-divider" />

      <h2 id="tracking">4. Tracking Script (Customer Websites)</h2>
      <p>When you install the ClickSaver tracking script on your website, it collects the following from your visitors — not for advertising, but solely for click fraud detection:</p>
      <ul>
        <li>IP address</li>
        <li>User agent (browser and OS information)</li>
        <li>Referring URL (where the click came from)</li>
        <li>Timestamp of the click</li>
        <li>Google Click ID (gclid) or Facebook Click ID (fbclid) from the URL</li>
      </ul>
      <p>The ClickSaver script does <strong>not</strong> set any cookies on your visitors' browsers. It operates entirely server-side. As a ClickSaver customer, you are responsible for disclosing this data collection in your own website's privacy policy.</p>

      <div className="legal-divider" />

      <h2 id="control">5. How to Control Cookies</h2>
      <p>You can control and manage cookies in several ways:</p>

      <h3>Browser Settings</h3>
      <p>Most browsers allow you to refuse or accept cookies, delete existing cookies, and set preferences for certain websites. Refer to your browser's help documentation:</p>
      <ul>
        <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
        <li><a href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
        <li><a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
        <li><a href="https://support.microsoft.com/en-us/windows/delete-and-manage-cookies" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
      </ul>
      <p>Please note that disabling essential cookies may affect the functionality of the ClickSaver dashboard.</p>

      <div className="legal-divider" />

      <h2 id="changes">6. Changes to This Policy</h2>
      <p>We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.</p>

      <div className="legal-divider" />

      <h2 id="contact">7. Contact Us</h2>
      <p>If you have questions about our use of cookies, please contact us:</p>
      <ul>
        <li>Email: <a href="mailto:support@clicksaver.app">support@clicksaver.app</a></li>
        <li>Website: <a href="https://clicksaver.io">clicksaver.io</a></li>
      </ul>

    </LegalPage>
  );
}
